import debounce from "lodash/debounce"
import flatMap from "lodash/flatMap"
import { denormalize, normalize } from "normalizr"

import DEFAULT_I18N from "~/i18n"
import { defaultCtx } from "~/lib/AjaxCtx"
import { ellipsis, ucfirst } from "~/lib/utils"

import { notify } from "~/lib/bugsnag"

import connectorModuleShard from "~/modules/plugins/connector/connectorModuleShard"
import fundaeModuleShard from "~/modules/plugins/fundae/fundaeModuleShard"
import pollModuleShard from "~/modules/plugins/poll/pollModuleShard"
import presentialModuleShard from "~/modules/plugins/presential/presentialModuleShard"
import tntModuleShard from "~/modules/plugins/tnt/tntModuleShard"

import SocialService from "~/components/social/SocialService"
import ConnectorService from "~/modules/plugins/connector/ConnectorService"
import FundaeService from "~/modules/plugins/fundae/FundaeService"
import PollService from "~/modules/plugins/poll/PollService"
import PresentialService from "~/modules/plugins/presential/PresentialService"
import TntService from "~/modules/plugins/tnt/TntService"
import { DEFAULT_USER_ACTIVITY } from "./constants"
import { userCallSchema } from "./schemas"

export function makeDefaultState() {
    const activitiesViewMode = "list"

    return {
        userActivities: {}, // userActivityId    => { configuration, state, localState }
        userCalls: {}, // userCallId        => UserCall
        levels: {}, // programActivityId => Level // TODO programActivityId? WTF

        virtualClassrooms: {}, // userCallId        => VirtualClassrooms
        forums: {}, // userCallId        => Forums

        /*
         * userCallId => {
         *   activityResources: { userActivityId => [Resource] },
         *   groupResources:    [Resource],
         *   programResources:  [Resource],
         *   toolsResources:    [Resource],
         * }
         */
        resources: {},

        classmates: {}, // userCallId        => [Page<User>]
        teachers: {}, // userCallId        => [Page<User>]

        events: {}, // userCallId        => [Event]

        fetchingClassmates: false,
        fetchingTeachers: false,
        activitiesViewMode,
    }
}

export const getters = {
    getDenormalizedUserCall: state => userCallId => {
        return denormalize(userCallId, userCallSchema, {
            userActivity: state.userActivities,
            level: state.levels,
            userCall: state.userCalls,
        })
    },

    getClassmates: state => userCallId => {
        const pages = state.classmates[userCallId] || []
        return flatMap(pages, p => p.items)
    },

    getTeachers: state => userCallId => {
        const pages = state.teachers[userCallId] || []
        return flatMap(pages, p => p.items)
    },

    classmatesEndReached: state => userCallId => {
        const pages = state.classmates[userCallId] || []
        return pages.length > 0 && !pages.at(-1).cursor.next
    },

    teachersEndReached: state => userCallId => {
        const pages = state.teachers[userCallId] || []
        return pages.length > 0 && !pages.at(-1).cursor.next
    },
}

export const mutations = {
    setUserCall(state, userCall) {
        if (!("userCallId" in userCall)) {
            throw new Error("Missing userCallId")
        }

        state.userCalls[userCall.userCallId] = userCall
    },

    setLevel(state, level) {
        state.levels[level.programActivityId] = level
    },

    setUserActivity(state, userActivity) {
        if (!("userActivityId" in userActivity)) {
            throw new Error("Missing userActivityId")
        }

        userActivity = Object.assign(
            {},
            DEFAULT_USER_ACTIVITY,
            state.userActivities[userActivity.userActivityId],
            userActivity
        )
        state.userActivities[userActivity.userActivityId] = userActivity
    },

    setVirtualClassrooms(state, { userCallId, virtualClassrooms }) {
        state.virtualClassrooms[userCallId] = virtualClassrooms
    },

    setForums(state, { userCallId, forums }) {
        state.forums[userCallId] = forums
    },

    updateForum(state, { userCallId, forum }) {
        const forums = state.forums[userCallId]
        const forumIndex = forums.findIndex(
            f => f.resourceRelationId == forum.resourceRelationId
        )
        forums[forumIndex] = forum
    },

    setResources(state, { userCallId, resources }) {
        state.resources[userCallId] = resources
    },

    setEvents(state, { userCallId, events }) {
        state.events[userCallId] = events
    },

    pushClassmatesPage(state, { userCallId, users, cursor }) {
        if (!(userCallId in state.classmates)) {
            state.classmates[userCallId] = []
        }

        state.classmates[userCallId].push({
            items: users,
            cursor,
        })
    },

    pushTeachersPage(state, { userCallId, users, cursor }) {
        if (!(userCallId in state.teachers)) {
            state.teachers[userCallId] = []
        }

        state.teachers[userCallId].push({
            items: users,
            cursor,
        })
    },

    setFetchingClassmates(state, val) {
        state.fetchingClassmates = val
    },

    setFetchingTeachers(state, val) {
        state.fetchingTeachers = val
    },

    setState(state, { userActivityId, activityState }) {
        const activity = state.userActivities[userActivityId]
        if (!activity)
            throw new Error(`Activity ${userActivityId} is not initialized.`)
        activity.state = activityState
    },

    setLocalState(state, { userActivityId, localState }) {
        const activity = state.userActivities[userActivityId]
        if (!activity)
            throw new Error(`Activity ${userActivityId} is not initialized.`)
        activity.localState = localState
    },

    clearLocalState(state, { userActivityId }) {
        const activity = state.userActivities[userActivityId]
        if (!activity)
            throw new Error(`Activity ${userActivityId} is not initialized.`)
        activity.localState = {}
    },

    clearModule(state) {
        Object.assign(state, makeDefaultState())
    },

    setActivitiesViewMode(state, viewMode) {
        state.activitiesViewMode = viewMode
    },
}

export function makeActions({ ctx, socialService, i18n }) {
    i18n = i18n ?? DEFAULT_I18N.global
    return {
        async refreshUserCall({ dispatch }, userCallId) {
            const denormalizedUserCall = await ctx.fetchData(
                `/user-calls/${userCallId}`,
                { waitFor: "/participant-activity/" }
            )
            dispatch("storeDenormalizedUserCall", denormalizedUserCall)
        },

        storeDenormalizedUserCall({ commit }, denormalizedUserCall) {
            const { entities } = normalize(denormalizedUserCall, userCallSchema)

            const levels = entities.level || {}
            for (let level of Object.values(levels)) {
                commit("setLevel", level)
            }

            const userActivities = entities.userActivity || {}
            for (let userActivity of Object.values(userActivities)) {
                commit("setUserActivity", userActivity)
            }

            const userCalls = entities.userCall || {}
            for (let userCall of Object.values(userCalls)) {
                commit("setUserCall", userCall)
            }
        },

        async syncActivity(
            { dispatch, rootGetters, rootState },
            { userActivityId, isClosing }
        ) {
            const { tabId } = rootState
            if (!rootGetters.currentUser) return // don't send the request once the user is logged out
            if (isClosing) {
                ctx.sendBeacon(
                    `/participant-activity/${userActivityId}/exiting`,
                    { tabId, isClosing }
                )
            } else {
                const denormalizedUserCall = await ctx.fetchData(
                    `/participant-activity/${userActivityId}/exiting`,
                    {
                        method: "POST",
                        body: JSON.stringify({ tabId, isClosing }),
                    }
                )
                dispatch("storeDenormalizedUserCall", denormalizedUserCall)
            }
        },

        async refreshUserActivity({ commit }, userActivityId) {
            const userActivity = await ctx.fetchData(
                `/participant-activity/${userActivityId}/info`
            )
            commit("setUserActivity", userActivity)
        },

        async openUserActivity({ commit }, { userActivityId }) {
            const url = `/participant-activity/${userActivityId}`
            const userActivity = await ctx.fetchData(url)
            commit("setUserActivity", userActivity)
        },

        async extendUserActivity({ commit, dispatch }, userActivityId) {
            const successExtensionMessage = ucfirst(
                i18n.t("common.extendActivitySuccess")
            )
            try {
                const userActivity = await ctx.fetchData(
                    `/user-calls-activities/${userActivityId}/extend`,
                    {
                        method: "PUT",
                    }
                )
                dispatch(
                    "pushNotification",
                    {
                        title: successExtensionMessage,
                        type: "success",
                    },
                    { root: true }
                )
                commit("setUserActivity", userActivity)
            } catch (e) {
                dispatch(
                    "pushNotification",
                    {
                        title: e.message,
                        type: "error",
                    },
                    { root: true }
                )
                notify(e)
            }
        },

        refreshResources: debounce(
            ({ dispatch }, params) => {
                return dispatch("_refreshResources", params)
            },
            1000,
            { leading: true, trailing: false }
        ),

        async _refreshResources({ commit }, userCallId) {
            const resources = await ctx.fetchData(
                `/user-calls/${userCallId}/resources`
            )
            commit("setResources", { userCallId, resources })
        },

        refreshVirtualClassrooms: debounce(
            ({ dispatch }, params) => {
                return dispatch("_refreshVirtualClassrooms", params)
            },
            1000,
            { leading: true, trailing: false }
        ),

        async _refreshVirtualClassrooms({ commit }, userCallId) {
            const virtualClassrooms = await ctx.fetchData(
                `/user-calls/${userCallId}/vc`
            )
            commit("setVirtualClassrooms", { userCallId, virtualClassrooms })
        },

        refreshForums: debounce(
            ({ dispatch }, params) => {
                return dispatch("_refreshForums", params)
            },
            1000,
            { leading: true, trailing: false }
        ),

        async _refreshForums({ commit }, userCallId) {
            const forums = await ctx.fetchData(
                `/user-calls/${userCallId}/forums`
            )
            commit("setForums", { userCallId, forums })
        },

        refreshEvents: debounce(
            ({ dispatch }, params) => {
                return dispatch("_refreshEvents", params)
            },
            1000,
            { leading: true, trailing: false }
        ),

        async _refreshEvents({ commit }, userCallId) {
            const events = await ctx.fetchData(
                `/user-calls/${userCallId}/events`
            )
            commit("setEvents", { userCallId, events })
        },

        getMoreClassmates: debounce(
            async ({ commit, dispatch }, params) => {
                commit("setFetchingClassmates", true)
                await dispatch("_getMoreClassmates", params)
                commit("setFetchingClassmates", false)
            },
            1000,
            { leading: true, trailing: false }
        ),

        getMoreTeachers: debounce(
            async ({ commit, dispatch }, params) => {
                commit("setFetchingTeachers", true)
                await dispatch("_getMoreTeachers", params)
                commit("setFetchingTeachers", false)
            },
            1000,
            { leading: true, trailing: false }
        ),

        async _getMoreTeachers({ state, commit }, userCallId) {
            let url = `/user-calls/${userCallId}/teachers?limit=50`

            const pages = state.teachers[userCallId] || []
            const lastPage = pages.at(-1)
            if (lastPage) {
                if (lastPage.cursor.next) {
                    url += `&cursor=${lastPage.cursor.next}`
                } else {
                    return
                }
            }

            const {
                data: users,
                meta: { cursor },
            } = await ctx.fetchAPI(url)

            commit("pushTeachersPage", { userCallId, users, cursor })
        },

        async _getMoreClassmates({ state, commit }, userCallId) {
            let url = `/user-calls/${userCallId}/classmates?limit=50`

            const pages = state.classmates[userCallId] || []
            const lastPage = pages.at(-1)
            if (lastPage) {
                if (lastPage.cursor.next) {
                    url += `&cursor=${lastPage.cursor.next}`
                } else {
                    return
                }
            }

            const {
                data: users,
                meta: { cursor },
            } = await ctx.fetchAPI(url)

            commit("pushClassmatesPage", { userCallId, users, cursor })
        },

        async sendBookmark({ state, commit, dispatch }, userCallId) {
            const userCall = state.userCalls[userCallId]
            if (!userCall) {
                throw new Error(`UserCall ${userCallId} doesn't exist`)
            }
            const { bookmarked } = userCall.social
            const originalUserCall = Object.assign({}, userCall)
            const social = Object.assign({}, userCall.social, {
                bookmarked: !bookmarked,
            })
            const optimisticUserCall = Object.assign({}, userCall, { social })
            commit("setUserCall", optimisticUserCall)

            try {
                const newUserCall =
                    await socialService.bookmarkResource(optimisticUserCall)
                commit("setUserCall", newUserCall)
                const info = newUserCall.social.bookmarked
                    ? i18n.t("common.wasAddedToBookmarks")
                    : i18n.t("common.wasRemovedFromBookmarks")

                dispatch(
                    "pushNotification",
                    {
                        title: ucfirst(ellipsis(newUserCall.programName, 75)),
                        info: ucfirst(info),
                        type: newUserCall.social.bookmarked
                            ? "success"
                            : "info",
                    },
                    { root: true }
                )
            } catch (error) {
                commit("setUserCall", originalUserCall)
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        async rateCall({ state, commit, dispatch }, { rating, userCallId }) {
            const userCall = state.userCalls[userCallId]
            if (!userCall) {
                throw new Error(`UserCall ${userCallId} doesn't exist`)
            }

            try {
                const newUserCall = await socialService.rateResource({
                    rating,
                    resource: userCall,
                })
                commit("setUserCall", newUserCall)
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        title: error.message,
                        type: "error",
                    },
                    { root: true }
                )
            }
        },

        async saveProgress(
            { state, commit, dispatch },
            { userActivityId, progress, playBackTime, currentTime }
        ) {
            const { meta = {} } = await ctx.fetchAPI(
                `/participant-activity/${userActivityId}/save`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        progress,
                        playBackTime,
                        currentTime,
                    }),
                }
            )
            const { ongoing } = meta
            if (ongoing) {
                dispatch(
                    "pushNotification",
                    {
                        title: i18n.t(
                            "plugins.videos.youAreCurrentlyWatchingTheFollowingVideos"
                        ),
                        info: ongoing.videoPlaying,
                        type: "warning",
                    },
                    { root: true }
                )
            }

            const activityState = {
                ...state.userActivities[userActivityId].state,
                progress,
                playBackTime,
                currentTime,
            }
            commit("setState", { userActivityId, activityState })
        },

        storeActivitiesViewMode({ commit }, viewMode) {
            commit("setActivitiesViewMode", viewMode)
        },
    }
}

export default {
    namespaced: true,

    state: makeDefaultState(),

    getters: {
        ...getters,
        ...pollModuleShard.getters,
        ...tntModuleShard.getters,
        ...presentialModuleShard.getters,
    },

    mutations: {
        ...mutations,
        ...fundaeModuleShard.mutations,
        ...pollModuleShard.mutations,
        ...tntModuleShard.mutations,
        ...presentialModuleShard.mutations,
    },

    actions: {
        ...makeActions({ ctx: defaultCtx, socialService: new SocialService() }),
        ...fundaeModuleShard.makeActions(new FundaeService(defaultCtx)),
        ...pollModuleShard.makeActions(new PollService(defaultCtx)),
        ...tntModuleShard.makeActions(new TntService(defaultCtx)),
        ...presentialModuleShard.makeActions(new PresentialService(defaultCtx)),
        ...connectorModuleShard.makeActions(new ConnectorService()),
    },
}
